/**
 * Marketplace checkout buttons — external links to Shopee / Tokopedia /
 * TikTok Shop. Renders nothing when no enabled links are provided.
 *
 * Features:
 *   - Per-platform brand colors + inline SVG logos
 *   - Opens in a new tab (`target="_blank"` + `rel="noopener,noreferrer"`)
 *   - Posts a click event to `/api/marketplace/click` for analytics
 *   - Mirrors the click as a GA4 `select_marketplace` event (consent-gated)
 *   - Mobile-sticky variant via the `sticky` prop
 *   - Hides any link with `enabled=false` or empty `url`
 */
import api from "../lib/api";
import { ga4Event } from "../lib/ga4";
import TX from "./TX";

// Brand registry — additional marketplaces can be added here without touching
// any other code. `key` matches the backend Literal.
export const MARKETPLACE_BRANDS = {
  shopee: {
    label: "Checkout via Shopee",
    short: "Shopee",
    bg: "#EE4D2D",
    fg: "#FFFFFF",
    // Simplified Shopee bag mark
    logo: (
      <svg viewBox="0 0 32 32" width="20" height="20" aria-hidden="true">
        <path
          d="M11 9c0-2.8 2.2-5 5-5s5 2.2 5 5v1h3.5l-1.4 14.8a3 3 0 0 1-3 2.7H11.9a3 3 0 0 1-3-2.7L7.5 10H11V9zm2 1h6V9a3 3 0 0 0-6 0v1zm-2 5c0 1.6 1 2.7 2.7 3.5 1.5.6 2.3 1 2.3 1.7 0 .8-.7 1.3-1.8 1.3-1 0-1.9-.5-2.6-1.2l-1.2 1.4c.9.9 2.3 1.6 3.7 1.6 2.2 0 3.8-1.2 3.8-3.2 0-1.7-1-2.7-2.9-3.4-1.4-.5-2.1-.9-2.1-1.7 0-.6.5-1.1 1.5-1.1.8 0 1.5.3 2.2.9l1.1-1.4c-.9-.8-2-1.2-3.3-1.2-2 0-3.4 1.1-3.4 2.9z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  tokopedia: {
    label: "Checkout via Tokopedia",
    short: "Tokopedia",
    bg: "#03AC0E",
    fg: "#FFFFFF",
    // Simplified Tokopedia cart-bag mark
    logo: (
      <svg viewBox="0 0 32 32" width="20" height="20" aria-hidden="true">
        <path
          d="M9 11h14l-1.2 12.6a3 3 0 0 1-3 2.7H13.2a3 3 0 0 1-3-2.7L9 11zm3-1V9a4 4 0 1 1 8 0v1h-2V9a2 2 0 1 0-4 0v1h-2zm1.5 6.2 2.5 3.4 2.5-3.4 1.5 1.1-3 4.1V25h-2v-3.6l-3-4.1 1.5-1.1z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  tiktok_shop: {
    label: "Checkout via TikTok Shop",
    short: "TikTok Shop",
    bg: "#000000",
    fg: "#FFFFFF",
    // Simplified TikTok music note
    logo: (
      <svg viewBox="0 0 32 32" width="20" height="20" aria-hidden="true">
        <path
          d="M22 5h-3.5v13.2a3.3 3.3 0 1 1-3.3-3.3v-3.6a6.9 6.9 0 1 0 6.9 6.9V11.6a8.1 8.1 0 0 0 4.6 1.4V9.4A4.6 4.6 0 0 1 22 5z"
          fill="currentColor"
        />
      </svg>
    ),
  },
};

const trackClick = async (gemstoneId, platform) => {
  // Backend log (fire-and-forget — never block the redirect)
  try { api.post("/marketplace/click", { gemstone_id: gemstoneId, platform }); } catch (e) { /* no-op */ }
  // GA4 mirror (consent-gated inside ga4Event)
  ga4Event("select_marketplace", {
    marketplace: platform,
    gemstone_id: gemstoneId,
  });
};

const openLink = (url) => {
  if (typeof window === "undefined") return;
  window.open(url, "_blank", "noopener,noreferrer");
};

const PlatformButton = ({ gemstoneId, link, sticky }) => {
  const brand = MARKETPLACE_BRANDS[link.platform];
  if (!brand) return null;
  const onClick = (e) => {
    e.preventDefault();
    trackClick(gemstoneId, link.platform);
    openLink(link.url);
  };
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      data-testid={`marketplace-btn-${link.platform}${sticky ? "-sticky" : ""}`}
      style={{ backgroundColor: brand.bg, color: brand.fg }}
      className={`flex items-center justify-center gap-2 font-accent uppercase tracking-[0.22em] text-[11px] transition-opacity hover:opacity-90 ${
        sticky ? "flex-1 px-3 py-3" : "px-5 py-4"
      }`}
    >
      <span className="inline-flex items-center" aria-hidden="true">{brand.logo}</span>
      <span>{sticky ? brand.short : brand.label}</span>
    </a>
  );
};

export const MarketplaceCheckoutButtons = ({ gemstoneId, links, sticky = false }) => {
  const enabled = (links || []).filter((l) => l && l.enabled !== false && l.url && l.url.trim());
  if (enabled.length === 0) return null;

  if (sticky) {
    return (
      <div
        data-testid="marketplace-sticky-bar"
        className="fixed bottom-0 inset-x-0 z-[60] bg-noir/95 backdrop-blur-md border-t border-gold/30 md:hidden"
      >
        <div className="flex items-stretch gap-2 px-3 py-3">
          {enabled.map((l) => (
            <PlatformButton key={l.platform} gemstoneId={gemstoneId} link={l} sticky />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="marketplace-buttons" className="space-y-3 mb-10">
      <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/40">
        <TX>Also available on</TX>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {enabled.map((l) => (
          <PlatformButton key={l.platform} gemstoneId={gemstoneId} link={l} />
        ))}
      </div>
    </div>
  );
};

export default MarketplaceCheckoutButtons;
